import React, { useState, useEffect, useRef } from 'react';
import { userFeedbackManager, buildMailtoUrl, type UserFeedback } from '../../utils/userFeedback';
import { createFocusTrap } from '../../utils/focusTrap';
import { X } from 'lucide-react';

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialType?: UserFeedback['type'];
  initialCategory?: string;
  initialError?: string;
}

const FEEDBACK_TYPES = [
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'general_feedback', label: 'General Feedback' },
  { value: 'usability_issue', label: 'Usability Issue' },
  { value: 'performance_issue', label: 'Performance Issue' },
];

const CATEGORIES = ['general', 'ui_ux', 'performance', 'bug', 'feature_request', 'accessibility'];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  isOpen,
  onClose,
  initialType = 'general_feedback',
  initialCategory = 'general',
  initialError,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState(false);
  const [formData, setFormData] = useState({
    type: initialType,
    title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
    description: initialError || '',
    category: initialCategory,
    priority: 'medium' as UserFeedback['priority'],
  });

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: initialType,
        title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
        description: initialError || '',
        category: initialCategory,
        priority: 'medium',
      });
      setSubmitted(false);
      setFeedbackId(null);
      setSubmitError(false);
    }
  }, [isOpen, initialType, initialCategory, initialError]);

  useEffect(() => {
    if (!isOpen || !contentRef.current) return;
    const cleanup = createFocusTrap(contentRef.current);
    return cleanup;
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { ...formData, url: window.location.href };
      const id = userFeedbackManager.submitFeedback(payload);
      setFeedbackId(id);
      const mailtoUrl = buildMailtoUrl(payload);
      if (mailtoUrl) window.location.href = mailtoUrl;
      setSubmitted(true);
    } catch (err) {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'var(--bg-overlay)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 'var(--z-modal)', padding: 'var(--space-md)',
    }}>
      <div ref={contentRef} role="dialog" aria-modal="true" aria-label="Send Feedback" style={{
        background: 'var(--bg-elevated)', borderRadius: 'var(--border-radius-lg)',
        boxShadow: 'var(--shadow-xl)', maxWidth: 672, width: '100%',
        maxHeight: '90vh', overflow: 'auto',
      }}>
        <div style={{ padding: 'var(--space-lg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
            <h2 style={{ fontSize: 'var(--font-size-xl)' }}>Send Feedback</h2>
            <button onClick={onClose} aria-label="Close" style={{ fontSize: 'var(--font-size-xl)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)' }}><X size={18} /></button>
          </div>

          {submitted ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-2xl) 0' }}>
              <p style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-sm)' }}>Thank you!</p>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
                Your mail client should have opened. Send the pre-filled email to complete.
              </p>
              {feedbackId && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-tertiary)', marginBottom: 'var(--space-md)' }}>Ref: {feedbackId}</p>}
              <button onClick={onClose} style={{ padding: 'var(--space-sm) var(--space-lg)', background: 'var(--color-primary)', color: 'white', borderRadius: 'var(--border-radius-sm)', border: 'none', cursor: 'pointer' }}>Close</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
              <div>
                <label htmlFor="fb-type" style={{ display: 'block', marginBottom: 4, fontSize: 'var(--font-size-sm)' }}>Type</label>
                <select id="fb-type" value={formData.type} onChange={(e) => handleChange('type', e.target.value)}
                  style={{ width: '100%', padding: 'var(--space-sm)', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', boxSizing: 'border-box' }}>
                  {FEEDBACK_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="fb-category" style={{ display: 'block', marginBottom: 4, fontSize: 'var(--font-size-sm)' }}>Category</label>
                <select id="fb-category" value={formData.category} onChange={(e) => handleChange('category', e.target.value)}
                  style={{ width: '100%', padding: 'var(--space-sm)', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', boxSizing: 'border-box' }}>
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              <div>
                <span style={{ display: 'block', marginBottom: 4, fontSize: 'var(--font-size-sm)' }}>Priority</span>
                <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
                  {PRIORITIES.map(p => (
                    <button key={p.value} type="button"
                      onClick={() => handleChange('priority', p.value)}
                      aria-pressed={formData.priority === p.value}
                      aria-label={`Priority: ${p.label}`}
                      style={{
                        padding: 'var(--space-xs) var(--space-sm)', borderRadius: 'var(--border-radius-sm)',
                        fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', cursor: 'pointer',
                        border: '1px solid', background: formData.priority === p.value ? 'var(--color-primary)' : 'transparent',
                        color: formData.priority === p.value ? 'white' : 'var(--text-primary)',
                        borderColor: formData.priority === p.value ? 'var(--color-primary)' : 'var(--border-color)',
                      }}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="fb-title" style={{ display: 'block', marginBottom: 4, fontSize: 'var(--font-size-sm)' }}>Title *</label>
                <input id="fb-title" type="text" required value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)} placeholder="Brief summary"
                  style={{ width: '100%', padding: 'var(--space-sm)', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label htmlFor="fb-description" style={{ display: 'block', marginBottom: 4, fontSize: 'var(--font-size-sm)' }}>Description *</label>
                <textarea id="fb-description" rows={5} required value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)} placeholder="Please describe in detail..."
                  style={{ width: '100%', padding: 'var(--space-sm)', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
              {submitError && (
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-error)' }}>
                  Failed to submit feedback. Please try again.
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-sm)', paddingTop: 'var(--space-md)', borderTop: '1px solid var(--border-color)' }}>
                <button type="button" onClick={onClose} style={{ padding: 'var(--space-sm) var(--space-lg)', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius-sm)', cursor: 'pointer', color: 'var(--text-primary)' }}>Cancel</button>
                <button type="submit"
                  disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
                  style={{ padding: 'var(--space-sm) var(--space-lg)', background: 'var(--color-primary)', color: 'white', borderRadius: 'var(--border-radius-sm)', border: 'none', cursor: 'pointer', opacity: isSubmitting ? 0.5 : 1 }}>
                  {isSubmitting ? 'Opening mail…' : 'Send Feedback'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

interface FeedbackButtonProps {
  onOpen: (type?: UserFeedback['type'], category?: string, error?: string) => void;
}

export const FeedbackButton: React.FC<FeedbackButtonProps> = ({ onOpen }) => (
  <button
    onClick={() => onOpen()}
    aria-label="Send feedback"
    title="Send feedback"
    style={{
      position: 'fixed', bottom: 'var(--space-lg)', right: 'var(--space-lg)',
      background: 'var(--color-primary)', color: 'white',
      padding: 'var(--space-md)', borderRadius: '50%',
      boxShadow: 'var(--shadow-lg)', border: 'none', cursor: 'pointer',
      zIndex: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  </button>
);
